import { mockToday as today } from "./today";

// Job-grain booked-order fixture that feeds the mock /views/jobs surface via
// bookedOrderToJob. The API BookedOrder type was retired with the raw
// booked-orders replica, so this is now a mock-only shape (the jobs view is
// what production actually exposes).
export interface MockBookedOrder {
  id: string;
  customer_id: string;
  estimate_id?: string | null;
  description: string;
  booking_amt: number;
  contribution_amt: number;
  order_booking_date: string;
  invoiced_date?: string | null;
  completed_date?: string | null;
  due_date?: string | null;
  finished_production_date?: string | null;
  spoilage?: number | null;
  job_type: string;
  job_family: string;
  /** Marketing category vocabulary (backlog #12c ad-hoc sales report) —
   * print / display (wide format) / mailing / merch / clothing / creative / other. */
  marketing_category: string;
  quantity: number;
  existing_book_or_new_business: "existing_book" | "new_business";
  size_band: "unknown" | "small" | "medium" | "large" | "extra_large";
  complexity: "unknown" | "high" | "medium" | "low";
  is_outworked: boolean;
  is_altered: boolean;
  market_id?: string | null;
  production_method: string;
  source_system_tharstern_job_number: string;
  source_company_id: number;
  /** The customer's own order/PO reference (`MainJobDetails.OrderNo`) —
   *  free text, and legitimately absent on some jobs. */
  customer_order_no?: string | null;
  /** The customer's second reference slot (Tharstern `CustomerRef2`), read
   *  live from Tharstern rather than the replica. Absent far more often
   *  than the first slot. */
  customer_second_ref?: string | null;
  /** `tharstern_jobs.ref1` — the sales rep pool code, e.g. "Pollard Account"
   *  when nobody is individually assigned. Distinct from
   *  `customer_order_no`/`customer_second_ref` above (the customer's own PO
   *  references) despite the live Tharstern read exposing both pairs under
   *  the same `customer_ref1`/`customer_ref2` API field names — see
   *  docs/features/job-workspace-redesign/01_product_brief.md, "Two
   *  reference slots". */
  sales_rep?: string | null;
  /** `tharstern_jobs.ref2` — the CSR pool code, e.g. "Group CSR"/"Online"
   *  when nobody is individually assigned. */
  csr?: string | null;
  /** Job lifecycle — surfaced as the per-row stock-model badge. */
  stock_model: "adhoc" | "pod" | "stock_customer" | "stock_ours";
  /** Tharstern closed the job's invoicing with nothing billed (every
   *  chargeable-work row `job_completed_unbilled`) — it will never be
   *  invoiced, so it sits in the jobs list's "Closed, not billed" bucket. */
  closed_not_billed?: boolean;
}

const twoWeeksAgo = new Date(new Date(today).getTime() - 14 * 86400000)
  .toISOString()
  .slice(0, 10);
const oneMonthAgo = new Date(new Date(today).getTime() - 30 * 86400000)
  .toISOString()
  .slice(0, 10);
const nextWeek = new Date(new Date(today).getTime() + 7 * 86400000)
  .toISOString()
  .slice(0, 10);

// Anchor fixture for the job workspace redesign (docs/features/
// job-workspace-redesign/) — job 738492, the real production job that
// prompted the work. Booked 7 Sep, required 10 Sep, invoiced 11 Sep.
const sevenDaysAgo = new Date(new Date(today).getTime() - 7 * 86400000)
  .toISOString()
  .slice(0, 10);
const fourDaysAgo = new Date(new Date(today).getTime() - 4 * 86400000)
  .toISOString()
  .slice(0, 10);
const threeDaysAgo = new Date(new Date(today).getTime() - 3 * 86400000)
  .toISOString()
  .slice(0, 10);

export const mockBookedOrders: MockBookedOrder[] = [
  {
    // In progress — booked recently, due next week, no invoice yet
    id: "ord_001",
    customer_id: "cust_012",
    estimate_id: "est_002",
    description:
      "Annual report 2024 — 300pp perfect bound, 500 copies, litho print on 130gsm silk",
    booking_amt: 8750,
    contribution_amt: 2340,
    order_booking_date: today,
    invoiced_date: null,
    completed_date: null,
    due_date: nextWeek,
    finished_production_date: null,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 200,
    existing_book_or_new_business: "existing_book",
    size_band: "large",
    complexity: "high",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "litho",
    source_system_tharstern_job_number: "48291",
    source_company_id: 1,
    stock_model: "adhoc",
    customer_order_no: "PO 6415",
    customer_second_ref: "CC-4412",
  },
  {
    // Invoiced — completed a fortnight ago
    id: "ord_002",
    customer_id: "cust_048",
    estimate_id: null,
    description:
      "Summer product catalogue — 48pp, A4, 5000 copies, digital with gloss laminate cover",
    booking_amt: 3640,
    contribution_amt: 980,
    order_booking_date: oneMonthAgo,
    invoiced_date: twoWeeksAgo,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: twoWeeksAgo,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 1000,
    existing_book_or_new_business: "existing_book",
    size_band: "medium",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "48187",
    source_company_id: 1,
    stock_model: "pod",
  },
  {
    // Awaiting invoice — production finished a fortnight ago, not yet billed
    id: "ord_003",
    customer_id: "cust_022",
    estimate_id: "est_007",
    description:
      "Trade show pull-up banners × 12, with carry cases — amended to add two extra banners after proof",
    booking_amt: 1290,
    contribution_amt: 510,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: null,
    spoilage: null,
    job_type: "wide_format",
    marketing_category: "display",
    job_family: "signage-display",
    quantity: 12,
    existing_book_or_new_business: "new_business",
    size_band: "small",
    complexity: "low",
    is_outworked: true,
    is_altered: true,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "48224",
    source_company_id: 1,
    stock_model: "stock_ours",
  },
  {
    // Closed, not billed — reprint of spoiled stock, closed in Tharstern with
    // nothing invoiced. Never moves to Invoiced.
    id: "ord_004",
    customer_id: "cust_048",
    estimate_id: null,
    description: "Reprint — 2,000 A5 leaflets, replacing a spoiled run",
    booking_amt: 340,
    contribution_amt: 0,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: twoWeeksAgo,
    spoilage: null,
    job_type: "litho",
    marketing_category: "print",
    job_family: "leaflets-flyers",
    quantity: 2000,
    existing_book_or_new_business: "existing_book",
    size_band: "small",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "litho",
    source_system_tharstern_job_number: "48231",
    source_company_id: 1,
    stock_model: "adhoc",
    closed_not_billed: true,
  },
  {
    // Anchor fixture for the CSR command-centre email<->job pairing journey
    // (docs/features/csr-command-centre/) — the inbox "Link to a job…" picker
    // (LinkEmailToJobDialog) searches this customer's jobs via
    // getJobsForIssuePicker, which sources from mockBookedOrders and needs a
    // job_no ("id" below) matching the job-comms fixture's #482913 (see
    // job-comms.ts and work-visibility.ts's cw-482913) for the picked job to
    // resolve consistently across the two fixtures.
    id: "482913",
    customer_id: "c-meridian-retail",
    estimate_id: null,
    description: "500 x A5 flyers — gloss, double-sided",
    booking_amt: 640,
    contribution_amt: 210,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: null,
    due_date: nextWeek,
    finished_production_date: null,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 500,
    existing_book_or_new_business: "existing_book",
    size_band: "small",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "482913",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  {
    // A second, older Meridian Retail job so the link-to-job picker's search
    // has more than one candidate to filter across for the anchor customer.
    id: "482780",
    customer_id: "c-meridian-retail",
    estimate_id: null,
    description: "Point-of-sale window posters × 20, A2, foamex-mounted",
    booking_amt: 890,
    contribution_amt: 305,
    order_booking_date: oneMonthAgo,
    invoiced_date: twoWeeksAgo,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: twoWeeksAgo,
    spoilage: null,
    job_type: "wide_format",
    marketing_category: "display",
    job_family: "signage-display",
    quantity: 20,
    existing_book_or_new_business: "existing_book",
    size_band: "medium",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "482780",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  {
    // A third Meridian Retail job — awaiting invoice, so the picker has a mix
    // of in-progress/completed candidates like the other seeded customers.
    id: "481955",
    customer_id: "c-meridian-retail",
    estimate_id: "est_014",
    description: "Loyalty scheme mailer — 8pp A5, litho, 12,000 copies",
    booking_amt: 2150,
    contribution_amt: 640,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: null,
    spoilage: null,
    job_type: "mailing",
    marketing_category: "mailing",
    job_family: "print-sheets",
    quantity: 12000,
    existing_book_or_new_business: "existing_book",
    size_band: "large",
    complexity: "medium",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "litho",
    source_system_tharstern_job_number: "481955",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  // ── Anchor fixtures for the merged job workspace mockup ────────────────
  // (docs/features/estimate-job-transformation/) — id === job_no, per the
  // "482913" convention above, so the mock adapter's getChargeableJobById
  // resolves directly from the Tharstern job number the page is keyed on.
  // See mock-data/work-visibility.ts and mock-data/prepress-production.ts
  // for the matching work-unit / production fixtures for these same three
  // job numbers.
  {
    // Released to production, in progress on the press floor.
    id: "741032",
    customer_id: "cust_012",
    estimate_id: "est_741032",
    description: "Membership renewal pack — 4pp A4, litho, 3,000 copies",
    booking_amt: 2380,
    contribution_amt: 690,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: null,
    due_date: nextWeek,
    finished_production_date: null,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 3000,
    existing_book_or_new_business: "existing_book",
    size_band: "medium",
    complexity: "medium",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "litho",
    source_system_tharstern_job_number: "741032",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  {
    // Delivered and invoiced — terminal state.
    id: "741048",
    customer_id: "cust_048",
    estimate_id: "est_741048",
    description: "Open day prospectuses — 24pp A4, digital, 1,500 copies",
    booking_amt: 4120,
    contribution_amt: 1360,
    order_booking_date: oneMonthAgo,
    invoiced_date: twoWeeksAgo,
    completed_date: twoWeeksAgo,
    due_date: twoWeeksAgo,
    finished_production_date: twoWeeksAgo,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 1500,
    existing_book_or_new_business: "existing_book",
    size_band: "medium",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "741048",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  {
    // Raised by a catalogue order rather than booked on by a CSR — the job
    // exists, the money does not.
    id: "738912",
    customer_id: "cust_012",
    estimate_id: null,
    description: "Letterheads — 1,000 copies",
    booking_amt: 209.09,
    contribution_amt: 74.2,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: null,
    due_date: nextWeek,
    finished_production_date: null,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 1000,
    existing_book_or_new_business: "existing_book",
    size_band: "small",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "738912",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  {
    // In production with a live exception + an open issue.
    id: "741057",
    customer_id: "cust_022",
    estimate_id: null,
    description: "Investor pitch decks — 16pp A4, digital, 250 copies",
    booking_amt: 1980,
    contribution_amt: 615,
    order_booking_date: twoWeeksAgo,
    invoiced_date: null,
    completed_date: null,
    due_date: twoWeeksAgo,
    finished_production_date: null,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 250,
    existing_book_or_new_business: "existing_book",
    size_band: "small",
    complexity: "medium",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "741057",
    source_company_id: 1,
    stock_model: "adhoc",
  },
  // Anchor fixture for the job workspace redesign (docs/features/
  // job-workspace-redesign/) — the real job that prompted the work.
  // Complete and invoiced: 30 ordered/produced/delivered/invoiced, £44.00,
  // booked 7 Sep, required 10 Sep, invoiced 11 Sep. See mock-data/
  // work-visibility.ts (stepper/timeline/stages), mock-data/estimates.ts
  // (est_738492), mock-data/job-invoices.ts (invoice 923395), mock-data/
  // tharstern-attachments.ts and mock-data/job-comms.ts for the matching
  // fixtures for this same job number.
  {
    id: "738492",
    customer_id: "c-capture-property",
    estimate_id: "est_738492",
    description:
      "CAP9092-BH360EST-PGLGBHVV 152240 - 69 The Cliff - J Stops Blandford Forum - A4 Tentcard - 30 copies",
    booking_amt: 44,
    contribution_amt: 15.4,
    order_booking_date: sevenDaysAgo,
    invoiced_date: threeDaysAgo,
    completed_date: fourDaysAgo,
    due_date: fourDaysAgo,
    finished_production_date: fourDaysAgo,
    spoilage: null,
    job_type: "print",
    marketing_category: "print",
    job_family: "print-sheets",
    quantity: 30,
    existing_book_or_new_business: "existing_book",
    size_band: "small",
    complexity: "low",
    is_outworked: false,
    is_altered: false,
    market_id: null,
    production_method: "digital",
    source_system_tharstern_job_number: "738492",
    source_company_id: 1,
    stock_model: "adhoc",
    // The customer's own PO reference (MainJobDetails.OrderNo) — distinct
    // from the job description above, which also happens to start
    // "CAP9092-" but is Tharstern's own job-naming convention, not this
    // field. Don't conflate the two.
    customer_order_no: "CAP9092-216094-BH360",
    sales_rep: "Pollard Account",
    csr: "Stuart Russell",
  },
];
